import type { Paragraph, Root, Text } from "mdast";
import type { Handler } from "mdast-util-to-hast";
import type { Node, Parent } from "unist";
import { visit } from "unist-util-visit";

export const loadingHandler: Handler = (h, node) => {
  return h(node, "loading", []);
};

const LOADING_REGEX = /^:::loading(.*):::$/;

const ALLOWED_ATTRS: Record<string, (v: string) => number | undefined> = {
  height: (v) => (/^\d+$/.test(v) ? parseInt(v, 10) : undefined),
  width: (v) => (/^\d+$/.test(v) ? parseInt(v, 10) : undefined),
};

function parseAttributes(attrsStr: string): Record<string, number> {
  const out: Record<string, number> = {};
  const trimmed = attrsStr.trim();
  if (!trimmed) return out;
  for (const part of trimmed.split(/\s+/)) {
    const eq = part.indexOf("=");
    if (eq === -1) continue;
    const key = part.slice(0, eq);
    const value = part.slice(eq + 1);
    const parser = ALLOWED_ATTRS[key];
    if (!parser) continue;
    const num = parser(value);
    if (num !== undefined) out[key] = num;
  }
  return out;
}

/** Mdast node type for the loading placeholder (custom). */
export interface LoadingNode {
  type: "loading";
  data?: {
    hProperties?: Record<string, number>;
  };
}

function getLoadingAttributes(
  node: unknown,
): { value: string; attrs: Record<string, number> } | null {
  if (
    !node ||
    typeof node !== "object" ||
    (node as Paragraph).type !== "paragraph"
  ) {
    return null;
  }

  const children = (node as Paragraph).children;

  if (!Array.isArray(children) || children.length !== 1) return null;

  const first = children[0];

  if ((first as Text).type !== "text") return null;

  const value = (first as Text).value;
  const match = value.match(LOADING_REGEX);

  if (!match) return null;

  return { value, attrs: parseAttributes(match[1]) };
}

/**
 * Remark plugin: replaces a paragraph that contains ":::loading:::" or
 * ":::loading height=32 count=5:::" with a custom "loading" node so it can
 * be rendered as a skeleton in the markdown. Attributes (e.g. height, count)
 * are passed through to the component via node.data.hProperties.
 */
export function remarkLoading() {
  return (tree: Root) => {
    visit(tree as Node, "paragraph", (node, index, parent: Parent) => {
      if (parent === null || index === undefined) return;

      const parsed = getLoadingAttributes(node);

      if (!parsed) return;

      const loadingNode: LoadingNode = {
        type: "loading",
        data:
          Object.keys(parsed.attrs).length > 0
            ? { hProperties: parsed.attrs }
            : undefined,
      };

      parent.children[index] = loadingNode;
    });
  };
}
